"use client";

/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
Command: npx gltfjsx@6.2.3 public/models/64f1a714fe61576b46f27ca2.glb -o src/components/Avatar.tsx -k -r public
*/

import { useAnimations, useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useEffect, useRef, useState } from "react";
import { Group, SkinnedMesh, AnimationMixer } from "three";
import { Leva } from 'leva';


import * as THREE from "three";
import { VISEMES } from "wawa-lipsync";
import { getLipsyncManager } from "../lib/lipsync";
import { useAvatar, EmotionType } from "../contexts/AvatarContext";
import { AvatarProps } from "../types/avatar";
import { defaultSkinnedMesh } from "./DefaultSkinnedMesh";

const facialExpressions: Record<EmotionType, Record<string, number>> = {
  default: {
    mouthPressLeft: 0.61,
    mouthPressRight: 0.41000000000000003,
  },
  smile: {
    browInnerUp: 0.17,
    eyeSquintLeft: 0.4,
    eyeSquintRight: 0.44,
    noseSneerLeft: 0.1700000727403593,
    noseSneerRight: 0.14000002836874015,
    mouthPressLeft: 0.61,
    mouthPressRight: 0.41000000000000003,
  },
  funnyFace: {
    jawLeft: 0.63,
    mouthPucker: 0.53,
    noseSneerLeft: 1,
    noseSneerRight: 0.39,
    mouthLeft: 1,
    eyeLookUpLeft: 1,
    eyeLookUpRight: 1,
    cheekPuff: 0.9999924982764238,
    mouthDimpleLeft: 0.414743888682652,
    mouthRollLower: 0.32,
    mouthSmileLeft: 0.35499733688813034,
    mouthSmileRight: 0.35499733688813034,
  },
  sad: {
    mouthFrownLeft: 1,
    mouthFrownRight: 1,
    mouthShrugLower: 0.78341,
    browInnerUp: 0.452,
    eyeSquintLeft: 0.72,
    eyeSquintRight: 0.75,
    eyeLookDownLeft: 0.5,
    eyeLookDownRight: 0.5,
    jawForward: 1,
  },
  surprised: {
    eyeWideLeft: 0.5,
    eyeWideRight: 0.5,
    jawOpen: 0.351,
    mouthFunnel: 1,
    browInnerUp: 1,
  },
  angry: {
    browDownLeft: 1,
    browDownRight: 1,
    eyeSquintLeft: 1,
    eyeSquintRight: 1,
    jawForward: 1,
    jawLeft: 1,
    mouthShrugLower: 1,
    noseSneerLeft: 1,
    noseSneerRight: 0.42,
    eyeLookDownLeft: 0.16,
    eyeLookDownRight: 0.16,
    cheekSquintLeft: 1,
    cheekSquintRight: 1,
    mouthClose: 0.23,
    mouthFunnel: 0.63,
    mouthDimpleRight: 1,
  },
};

export function Avatar({
  skinnedMesh = defaultSkinnedMesh,
  modelPath = "/models/64f1a714fe61576b46f27ca2.glb",
  animationPath = "/models/animations_female.glb",
  ...props
}: AvatarProps) {
  const gltfResult = useGLTF(modelPath) as unknown as {
    nodes: Record<string, THREE.Object3D & Partial<SkinnedMesh>>;
    materials: Record<string, THREE.Material>;
    scene: THREE.Group;
  };
  const { nodes, materials, scene } = gltfResult;

  const { currentAnimation, currentEmotion } = useAvatar();

  const smoothMovements = true; // Enable smooth movements by default

  const { animations } = useGLTF(animationPath);

  const group = useRef<Group>(null);
  const { actions, mixer } = useAnimations(animations, group);

  // Use the animation from context
  useEffect(() => {
    if (actions[currentAnimation]) {
      // to log the duration of the current animation
      // console.log("currentAnimation", actions[currentAnimation].getClip().duration, currentAnimation);
      actions[currentAnimation]
        ?.reset()
        .fadeIn((mixer as AnimationMixer & { stats?: { actions?: { inUse?: number } } })?.stats?.actions?.inUse === 0 ? 0 : 0.5)
        .play();
      return () => {
        actions[currentAnimation]?.fadeOut(0.5);
      };
    }
  }, [currentAnimation, actions, mixer]);

  // Signal loaded state
  useEffect(() => {
    if (nodes && props.onLoaded) {
      props.onLoaded();
    }
  }, [nodes, props.onLoaded]);

  const lerpMorphTarget = (target: string, value: number, speed = 0.1) => {
    scene.traverse((child: THREE.Object3D) => {
      const skinnedMesh = child as SkinnedMesh;
      if (skinnedMesh.isSkinnedMesh && skinnedMesh.morphTargetDictionary) {
        const index = skinnedMesh.morphTargetDictionary[target];
        if (
          index === undefined ||
          skinnedMesh.morphTargetInfluences?.[index] === undefined
        ) {
          return;
        }
        skinnedMesh.morphTargetInfluences[index] = THREE.MathUtils.lerp(
          skinnedMesh.morphTargetInfluences[index],
          value,
          speed
        );

      }
    });
  };

  const [blink, setBlink] = useState<boolean>(false);
  // Unused wink states - can be used for future winking functionality
  // const [_winkLeft, _setWinkLeft] = useState<boolean>(false);
  // const [_winkRight, _setWinkRight] = useState<boolean>(false);

  useFrame(() => {
    // Handle facial expressions - only apply to emotion-specific morph targets
    const eyeLeft = nodes['EyeLeft'] as SkinnedMesh;
    const wolfHead = nodes['Wolf3D_Head'] as SkinnedMesh;

    // Use EyeLeft first, then Wolf3D_Head if EyeLeft doesn't have morph targets
    const emotionMesh = eyeLeft?.morphTargetDictionary ? eyeLeft : wolfHead;

    if (emotionMesh?.morphTargetDictionary) {
      // Only apply emotion morph targets that exist in our facial expressions
      const mapping = facialExpressions[currentEmotion];
      if (mapping) {
        Object.keys(mapping).forEach((key) => {
          if (emotionMesh.morphTargetDictionary![key] !== undefined) {
            lerpMorphTarget(key, mapping[key], 0.1);
          }
        });
      }

      // Reset emotion morph targets that are not in current emotion
      Object.keys(emotionMesh.morphTargetDictionary).forEach((key) => {
        if (key === "eyeBlinkLeft" || key === "eyeBlinkRight") {
          return; // eyes wink/blink are handled separately
        }
        const mapping = facialExpressions[currentEmotion];
        if (!mapping || !(key in mapping)) {
          lerpMorphTarget(key, 0, 0.1);
        }
      });
    }

    lerpMorphTarget("eyeBlinkLeft", blink ? 1 : 0, 0.5);
    lerpMorphTarget("eyeBlinkRight", blink ? 1 : 0, 0.5);

    // LIPSYNC
    try {
      let viseme = "";
      let smooth: boolean | number = smoothMovements;

      // Check for override first (from text-to-lipsync)
      if (props.visemeOverride) {
        viseme = props.visemeOverride;
        // Enable smoothing for text-to-lipsync too, but maybe slightly faster than audio to catch short cues
        smooth = true;
      } else {
        // Fallback to audio lipsync
        const lipsyncManager = getLipsyncManager();
        viseme = lipsyncManager.viseme;
        const state = (lipsyncManager as any).state;
        smooth = smoothMovements ? (state === "vowel" ? 0.2 : 0.4) : 1;
      }

      lerpMorphTarget(
        viseme,
        1,
        smooth ? 0.2 : 1
      );

      Object.values(VISEMES).forEach((value) => {
        if (viseme === value) {
          return;
        }
        lerpMorphTarget(
          value,
          0,
          smooth ? 0.1 : 1
        );
      });
    } catch (error) {
      // Lipsync not available (SSR or client-side error)
      // This is expected during SSR
    }
  });

  useEffect(() => {
    let blinkTimeout: NodeJS.Timeout;
    const nextBlink = () => {
      blinkTimeout = setTimeout(() => {
        setBlink(true);
        setTimeout(() => {
          setBlink(false);
          nextBlink();
        }, 200);
      }, THREE.MathUtils.randInt(1000, 5000));
    };
    nextBlink();
    return () => clearTimeout(blinkTimeout);
  }, []);

  return skinnedMesh(nodes, materials, props, group);
}

// Preload default models
useGLTF.preload("/models/64f1a714fe61576b46f27ca2.glb");
useGLTF.preload("/models/animations_female.glb");
